import React from 'react';
import { Typography } from '@mui/material';
import MssTooltip from './MssTooltip';
import { truncateTitle } from './helpers';

/**
 * A generated resource's name, cut short when it runs long.
 *
 * What was cut is never simply lost: hovering a shortened name shows the whole
 * of it, and a screen reader is given the whole of it in place of the shortened
 * one. Used wherever a resource is named — the page heading, the breadcrumb
 * trail, a row in the table — so a long name is cut the same way everywhere.
 *
 * Props:
 *   id          (string) - The element's ID.
 *   className   (string) - The class the page styles the name with.
 *   component   (string) - What to render it as. Defaults to a span.
 *   name        (string) - The resource's name.
 *   suffix      (string) - Optional; what the page adds after the name, such as
 *                          " Results". Never cut, since it is the page, not the
 *                          name.
 *   isFocusable (bool)   - Whether a keyboard can land here to read the tooltip.
 *                          False where something focusable already wraps it,
 *                          such as a breadcrumb link or a table cell.
 *   variant     (string) - MUI type variant. Pass "inherit" where this sits
 *                          inside text that has already been sized — a
 *                          breadcrumb, say — so it does not reset to body1.
 */
export const TruncatedTitle = ({
  id,
  className,
  component = 'span',
  variant,
  name,
  suffix = '',
  isFocusable = true,
}) => {
  const { text, isTruncated } = truncateTitle(name);
  const fullTitle = `${name}${suffix}`;

  const title = (
    <Typography
      className={className}
      component={component}
      variant={variant}
      id={id}
      /*
       * The shortened name is for the eye only: screen readers are read the
       * whole thing, and tabbing here is how a keyboard asks for the tooltip.
       */
      aria-label={isTruncated ? fullTitle : undefined}
      tabIndex={isTruncated && isFocusable ? 0 : undefined}
    >
      {text}
      {suffix}
    </Typography>
  );

  if (!isTruncated) return title;

  // enterTouchDelay: a tap shows the name rather than a long press, since a
  // touch screen has no hover to ask with.
  return (
    <MssTooltip id={`${id}__tooltip`} title={fullTitle} enterTouchDelay={0}>
      {title}
    </MssTooltip>
  );
};
